import { createContext, useContext, useMemo, ReactNode } from "react";
import type { Stripe } from "@stripe/stripe-js";

interface StripeContextValue {
  stripePromise: Promise<Stripe | null> | null;
  isConfigured: boolean;
}

const StripeContext = createContext<StripeContextValue | undefined>(undefined);

interface StripeProviderProps {
  children: ReactNode;
}

// Lazy load Stripe - only import the module when actually needed
// This prevents @stripe/stripe-js from being in the initial bundle
let stripeModulePromise: Promise<typeof import("@stripe/stripe-js")> | null =
  null;
const getStripeModule = () => {
  if (!stripeModulePromise) {
    stripeModulePromise = import("@stripe/stripe-js");
  }
  return stripeModulePromise;
};

export function StripeProvider({ children }: StripeProviderProps): JSX.Element {
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  // Validate Stripe publishable key format
  const isValidKey = useMemo(() => {
    if (!publishableKey || typeof publishableKey !== "string") {
      return false;
    }

    const trimmedKey = publishableKey.trim();
    if (!trimmedKey) {
      return false;
    }

    // Check if key starts with pk_test_ or pk_live_
    return (
      trimmedKey.startsWith("pk_test_") || trimmedKey.startsWith("pk_live_")
    );
  }, [publishableKey]);

  // Load Stripe lazily - only when actually needed
  const stripePromise = useMemo(() => {
    if (!isValidKey) {
      if (import.meta.env.DEV) {
        if (!publishableKey || !publishableKey.trim()) {
          console.warn("Stripe publishable key not configured. Set VITE_STRIPE_PUBLISHABLE_KEY in your environment.",
          );
        } else {
          console.warn("Invalid Stripe publishable key format. Key must start with pk_test_ or pk_live_",
          );
        }
      }
      return null;
    }

    // Dynamically import and call loadStripe
    return getStripeModule().then(({ loadStripe }) =>
      loadStripe(publishableKey),
    );
  }, [publishableKey, isValidKey]);

  const value = useMemo(
    () => ({
      stripePromise,
      isConfigured: isValidKey,
    }),
    [stripePromise, isValidKey],
  );

  return (
    <StripeContext.Provider value={value}>{children}</StripeContext.Provider>
  );
}

export function useStripeContext(): StripeContextValue {
  const context = useContext(StripeContext);

  if (context === undefined) {
    throw new Error("useStripeContext must be used within a StripeProvider");
  }

  return context;
}
